import React, { useCallback, useRef, useState } from 'react';
import { IconButton, Text, Popover } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import DotIcon from '../../assets/icons/DotIcon';
import { COLORS } from '../../styles/variables';
import { RadioWrapper, Label, Radio, HiddenInput, Count } from './RadioButtonSelector.styles';
import { device } from '../../styles/devices';

interface OptionProps {
  name: string;
  value?: string;
  label?: string | React.ReactNode;
  count?: number;
  ariaLabel?: string;
  disabled?: boolean;
  onChange: (isNonAudiFilter: boolean | undefined, option: string) => void;
  checked: boolean;
  popoverTitle?: string;
  popoverDescription?: string;
  isNonAudiFilter?: boolean;
  isVehiclesLoading?: boolean;
}

const Content = styled.div`
  @media ${device.tablet} {
    width: 290px;
  }
`;

const Option: React.FC<OptionProps> = ({
  value,
  onChange,
  name,
  label,
  count,
  ariaLabel,
  disabled = false,
  checked,
  popoverTitle,
  popoverDescription,
  isNonAudiFilter,
  isVehiclesLoading,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const inputName = `${name}-${value}`;
  const handleChange = useCallback(() => {
    if (!disabled && value) onChange(isNonAudiFilter, value);
  }, [disabled, value, onChange, isNonAudiFilter]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== ' ') return;
      e.preventDefault();
      handleChange();
    },
    [handleChange],
  );

  const optionRef = useRef(null);
  const iconRef = useRef(null);

  const position = {
    container: { bottom: '-86px' },
    arrow: {
      left: '68%',
    },
  };

  const countString = count?.toString();

  return (
    <RadioWrapper disabled={disabled} ref={optionRef}>
      <Radio
        tabIndex={0}
        onClick={handleChange}
        role="radio"
        aria-label={ariaLabel}
        aria-checked={checked}
        onKeyDown={handleKeyPress}
        checked={checked}
      >
        {checked ? <DotIcon color={COLORS.grey80} /> : <DotIcon color={COLORS.grey15} size={8} />}
      </Radio>
      <HiddenInput
        id={inputName}
        type="radio"
        onChange={handleChange}
        checked={checked}
        name={inputName}
      />
      <Label htmlFor={inputName}>
        {label}
        {countString && !isVehiclesLoading && <Count>({countString})</Count>}
        {popoverDescription && (
          <Popover
            aria-label={`filter_status_used_${inputName}_popover`}
            id={`filter_status_used_${inputName}_popover`}
            triggerElement={
              <IconButton
                variant="tertiary"
                icon="help-circle"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
              />
            }
            open={isOpen}
            content={
              <Content>
                <Text>{popoverDescription}</Text>
              </Content>
            }
            onClose={() => setIsOpen(false)}
            withTip
          />
        )}
      </Label>
    </RadioWrapper>
  );
};

export default Option;
